/*
  Rinolux theme script: load animations, the video player with a play button, and the slider used by
  featured collections, testimonials, the video slider and the collection list.
*/
(() => {
  const mobileQuery = window.matchMedia('(max-width: 749px)');

  /* Video with a play button. Muted autoplay videos keep their source in data-src until they are visible. */
  class PtVideo extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.video = this.querySelector('video');
      this.button = this.querySelector('.internal-video__play');
      this.sound = this.querySelector('.internal-video__sound-btn');
      this.timeline = this.querySelector('.internal-video__timeline');
      if (!this.video) return;
      this.button?.addEventListener('click', (e) => {
        e.preventDefault();
        this.toggle();
      });
      this.sound?.addEventListener('click', (e) => {
        e.preventDefault();
        this.video.muted = !this.video.muted;
        this.classList.toggle('internal-video--muted', this.video.muted);
      });
      this.video.addEventListener('play', () => this.classList.add('internal-video--playing'));
      this.video.addEventListener('pause', () => this.classList.remove('internal-video--playing'));
      this.video.addEventListener('waiting', () => this.classList.add('internal-video--loading'));
      this.video.addEventListener('playing', () => this.classList.remove('internal-video--loading'));
      this.video.addEventListener('ended', () => {
        if (!this.video.loop) this.classList.remove('internal-video--playing');
      });
      if (this.timeline) {
        this.video.addEventListener('timeupdate', () => {
          const d = this.video.duration || 1;
          this.timeline.style.setProperty('--completed', `${(this.video.currentTime / d) * 100}%`);
        });
        this.timeline.addEventListener('click', (e) => {
          const r = this.timeline.getBoundingClientRect();
          this.video.currentTime = ((e.clientX - r.left) / r.width) * (this.video.duration || 0);
        });
      }
      if (this.dataset.autoplay === 'true') this.autoplayWhenVisible();
    }

    loadSources() {
      let changed = false;
      this.video.querySelectorAll('source[data-src]').forEach((s) => {
        s.src = s.dataset.src;
        s.removeAttribute('data-src');
        changed = true;
      });
      if (changed) this.video.load();
    }

    autoplayWhenVisible() {
      const play = () => {
        this.loadSources();
        this.video.muted = true;
        this.video.play().catch(() => {});
      };
      if (!('IntersectionObserver' in window)) return play();
      const io = new IntersectionObserver((entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          io.disconnect();
          play();
        }
      });
      io.observe(this);
    }

    toggle() {
      this.loadSources();
      if (this.video.paused) this.video.play().catch(() => {});
      else this.video.pause();
    }

    // Called by the slider when the slide is no longer visible
    inactive() {
      const action = this.dataset.actionOnInactive;
      if (action === 'pause' && !this.video.paused) this.video.pause();
      if (action === 'mute') {
        this.video.muted = true;
        this.classList.add('internal-video--muted');
      }
    }
  }
  if (!customElements.get('internal-video')) customElements.define('internal-video', PtVideo);

  /*
    Slider: native scroll with snap points, arrows and dots like the old theme.
    It turns into a plain grid on a breakpoint when the section disables the slider there
    (rl-slider--off-desktop / rl-slider--off-mobile).
  */
  const ARROW =
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40" width="40" height="40" focusable="false" aria-hidden="true"><path d="m15.5 0.932-4.3 4.38 14.5 14.6-14.5 14.5 4.3 4.4 14.6-14.6 4.4-4.3-4.4-4.4-14.6-14.6z"></path></svg>';

  class PtSlider extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.track = this.querySelector('.rl-slider__track');
      this.list = this.querySelector('.rl-slider__list');
      this.slides = Array.from(this.list.children);
      this.dots = this.querySelector('.rl-slider__dots');
      this.arrows = this.querySelector('.rl-slider__arrows');
      this.page = 0;
      this.buildArrows();
      this.onScroll = this.onScroll.bind(this);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.resize = new ResizeObserver(() => this.update());
      this.resize.observe(this.track);
      mobileQuery.addEventListener('change', () => this.update());
      this.update();
      if (this.dataset.autoplay === 'true') this.startAutoplay();
    }

    disconnectedCallback() {
      this.resize?.disconnect();
      clearInterval(this.timer);
    }

    buildArrows() {
      if (!this.arrows || this.arrows.children.length) return;
      const color = this.dataset.arrowsColor ? ` color-${this.dataset.arrowsColor}` : '';
      this.arrows.innerHTML = `
        <button type="button" class="rl-slider__arrow rl-slider__arrow--prev${color}" aria-label="${this.dataset.prevLabel || 'Previous'}">${ARROW}</button>
        <button type="button" class="rl-slider__arrow rl-slider__arrow--next${color}" aria-label="${this.dataset.nextLabel || 'Next'}">${ARROW}</button>`;
      this.prev = this.arrows.querySelector('.rl-slider__arrow--prev');
      this.next = this.arrows.querySelector('.rl-slider__arrow--next');
      this.prev.addEventListener('click', () => this.go(this.page - 1));
      this.next.addEventListener('click', () => this.go(this.page + 1));
    }

    get active() {
      return !this.classList.contains(mobileQuery.matches ? 'rl-slider--off-mobile' : 'rl-slider--off-desktop');
    }

    perPage() {
      const style = getComputedStyle(this);
      const v = parseFloat(style.getPropertyValue(mobileQuery.matches ? '--columns-mobile' : '--columns-desktop'));
      return Math.max(1, v || 1);
    }

    perMove() {
      const v = parseInt(mobileQuery.matches ? this.dataset.perMoveMobile : this.dataset.perMoveDesktop, 10);
      return Math.max(1, v || 1);
    }

    update() {
      const visible = this.slides.filter((s) => s.offsetParent !== null);
      this.visibleSlides = visible;
      const per = Math.floor(this.perPage());
      const move = this.perMove();
      const overflow = this.active && visible.length > per;
      this.classList.toggle('is-overflow', overflow);
      this.pages = overflow ? Math.ceil((visible.length - per) / move) + 1 : 1;
      this.buildDots();
      this.page = Math.min(this.page, this.pages - 1);
      this.refresh();
    }

    buildDots() {
      if (!this.dots) return;
      if (this.dots.children.length === this.pages) return;
      const color = this.dataset.dotsColor ? ` dots-custom-color color-${this.dataset.dotsColor}` : '';
      this.dots.innerHTML = '';
      if (this.pages < 2) return;
      for (let i = 0; i < this.pages; i++) {
        const li = document.createElement('li');
        const b = document.createElement('button');
        b.type = 'button';
        b.className = `rl-slider__dot${color}`;
        b.setAttribute('aria-label', `${i + 1} / ${this.pages}`);
        b.addEventListener('click', () => this.go(i));
        li.appendChild(b);
        this.dots.appendChild(li);
      }
    }

    slideOffset(index) {
      const slide = this.visibleSlides[index];
      const first = this.visibleSlides[0];
      if (!slide || !first) return 0;
      return slide.offsetLeft - first.offsetLeft;
    }

    go(page) {
      if (!this.active) return;
      const p = Math.max(0, Math.min(this.pages - 1, page));
      const index = Math.min(p * this.perMove(), this.visibleSlides.length - Math.floor(this.perPage()));
      this.page = p;
      const max = this.track.scrollWidth - this.track.clientWidth;
      const left = p === this.pages - 1 ? max : Math.min(max, this.slideOffset(Math.max(0, index)));
      this.track.scrollTo({ left, behavior: 'smooth' });
      this.refresh();
      this.restartAutoplay();
    }

    onScroll() {
      if (!this.active || this.pages < 2) return;
      cancelAnimationFrame(this.raf);
      this.raf = requestAnimationFrame(() => {
        const max = this.track.scrollWidth - this.track.clientWidth;
        const x = this.track.scrollLeft;
        let page;
        if (x >= max - 2) page = this.pages - 1;
        else {
          const w = this.visibleSlides[1] ? this.slideOffset(1) - this.slideOffset(0) : this.track.clientWidth;
          page = Math.round(x / (w * this.perMove() || 1));
        }
        if (page !== this.page) {
          this.page = Math.max(0, Math.min(this.pages - 1, page));
          this.refresh();
        }
      });
    }

    refresh() {
      if (this.dots) {
        Array.from(this.dots.querySelectorAll('.rl-slider__dot')).forEach((d, i) => {
          d.classList.toggle('is-active', i === this.page);
          d.setAttribute('aria-current', i === this.page ? 'true' : 'false');
        });
      }
      if (this.prev) this.prev.disabled = this.page === 0;
      if (this.next) this.next.disabled = this.page >= this.pages - 1;
      const per = Math.floor(this.perPage());
      const first = Math.min(this.page * this.perMove(), Math.max(0, (this.visibleSlides || []).length - per));
      (this.visibleSlides || []).forEach((s, i) => {
        const on = !this.active || (i >= first && i < first + per);
        s.classList.toggle('is-visible', on);
        if (!on) s.querySelectorAll('internal-video').forEach((v) => v.inactive && v.inactive());
      });
      this.adaptHeight(first);
    }

    adaptHeight(first) {
      const key = mobileQuery.matches ? 'adaptiveMobile' : 'adaptiveDesktop';
      if (this.dataset[key] !== 'true' || !this.active) {
        this.track.style.removeProperty('height');
        return;
      }
      const slide = this.visibleSlides[first];
      if (slide) this.track.style.height = `${slide.firstElementChild ? slide.firstElementChild.offsetHeight : slide.offsetHeight}px`;
    }

    startAutoplay() {
      const ms = (parseFloat(this.dataset.autoplaySpeed) || 5) * 1000;
      clearInterval(this.timer);
      this.timer = setInterval(() => {
        if (!this.active || this.matches(':hover')) return;
        this.go(this.page >= this.pages - 1 ? 0 : this.page + 1);
      }, ms);
    }

    restartAutoplay() {
      if (this.dataset.autoplay === 'true') this.startAutoplay();
    }
  }
  if (!customElements.get('rl-slider')) customElements.define('rl-slider', PtSlider);
})();
